import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Clock, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { lt } from "../localize";
import type { Lesson, LessonProgress } from "../types";

interface LessonCardProps {
  lesson: Lesson;
  progress?: LessonProgress;
  locked?: boolean;
}

export const LessonCard = ({ lesson, progress, locked }: LessonCardProps) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const isCompleted = progress?.completed ?? false;

  return (
    <button
      className={cn(
        "w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-colors",
        locked
          ? "opacity-40 cursor-not-allowed"
          : "hover:bg-muted/50 active:bg-muted",
      )}
      onClick={() => !locked && navigate(`/learn/${lesson.id}`)}
      disabled={locked}
    >
      <div
        className={cn(
          "w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-xs font-bold",
          isCompleted
            ? "bg-accent text-white"
            : "bg-muted text-muted-foreground",
        )}
      >
        {isCompleted ? (
          <CheckCircle2 className="w-4 h-4" />
        ) : (
          lesson.order + 1
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{lt(lesson.title, lang)}</div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Clock className="w-3 h-3" />
          <span>{lesson.estimatedMinutes} min</span>
        </div>
      </div>

      {!locked && <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
    </button>
  );
};
